import React, { Suspense, useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ToastContainer } from "react-toastify";
import i18n from "./i18n";

// Import validators (Yup extensions)
import './validators';

// Import scss
import "./assets/scss/theme.scss";
import "./assets/scss/custom.scss";

// Import css
import "react-toastify/dist/ReactToastify.css";
import "simplebar-react/dist/simplebar.min.css";
import "react-datepicker/dist/react-datepicker.css";
import "./App.css";

import { AuthUserInfo, IsLockScreen, RouteTitles } from "./constants";
import { updateAppTitle } from "./helpers";
import LocalstorageService from "./helpers/localstorage-service";

import { changeRTLLayout } from "./store/slices/Layout/layoutSlice";
import { setAuthToken, setAuthUser } from "./store/slices/Auth/authSlice";

// Layouts — נטענים סינכרוני (קריטיים לכל דף)
import NonAuthLayout from "./components/Layout/NonAuthLayout";
import VerticalLayout from "./components/Layout/VerticalLayout";
import SysAdminLayout from "./components/Layout/SysAdminLayout";

// Auth — נטענים סינכרוני (דפים ראשונים)
import RouteAuthGuard from "./components/RouteAuthGuard";
import ModuleRouteGuard from "./components/ModuleRouteGuard";
import Login from "./pages/Authentication/Login";

// רכיב טעינה לזמן שה-chunk נטען
const PageLoader = () => (
  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">טוען...</span>
    </div>
  </div>
);

// Part C (31/05/2026) — redirect מ-nuances של V2 (`/xxx-v2` או `/xxx-v2/:id`) אל הנתיב הקנוני
// משמש כ-fallback ל-bookmarks ישנים אחרי קיבוע מסכי V2. להסרה אחרי 1-2 גרסאות.
const LegacyV2Redirect = () => {
  const location = useLocation();
  const target = location.pathname.replace(/-v2(\/|$)/g, '$1');
  return <Navigate to={target + location.search + location.hash} replace />;
};

// Part C (31/05/2026) — D.2: /sysadmin/bank/* → /bank/* (eliminate nested duplication)
const SysAdminBankRedirect = () => {
  const location = useLocation();
  const target = location.pathname.replace('/sysadmin/bank/', '/bank/');
  return <Navigate to={target + location.search + location.hash} replace />;
};

// ═══════════════════════════════════════════════════════════════
// Lazy imports — כל דף נטען רק כשניגשים אליו (code-splitting)
// ═══════════════════════════════════════════════════════════════

// Auth
const ForgotPassword = React.lazy(() => import("./pages/Authentication/ForgotPasword"));
const ResetPassword = React.lazy(() => import("./pages/Authentication/ResetPasword"));
const VerifyEmail = React.lazy(() => import("./pages/Authentication/VerifyEmail"));
const Lockscreen = React.lazy(() => import("./pages/Authentication/Lockscreen"));

// Account
const UserProfile = React.lazy(() => import("./pages/Account/UserProfile"));

// Dashboard V2 (Part C — legacy Dashboard removed)

// Bank (parent layout — V2 leaves below)
const Bank = React.lazy(() => import("./pages/Bank"));

// Company
const Company = React.lazy(() => import("./pages/Company"));

// Admin V2 lazy imports below (V2 components only — all legacy removed in Part C)
const AlertsHubV2 = React.lazy(() => import("./pages/Admin/AlertsHubV2"));
const SpecialDaysConfigV2 = React.lazy(() => import("./pages/Admin/SpecialDaysConfigV2"));
const CompanyParametersV2 = React.lazy(() => import("./pages/Admin/CompanyParametersV2"));
const TerminalOperationCorrectionV2 = React.lazy(() => import("./pages/Admin/TerminalOperationCorrectionV2"));
const SendMessageV2 = React.lazy(() => import("./pages/Admin/SendMessageV2"));

// Phase 13 — Bank V2
const BankListV2 = React.lazy(() => import("./pages/Bank/BankListV2"));
const AddBankV2 = React.lazy(() => import("./pages/Bank/AddBankV2"));
const UpdateBankV2 = React.lazy(() => import("./pages/Bank/UpdateBankV2"));
const BankBranchV2 = React.lazy(() => import("./pages/Bank/BankBranchV2"));

// Phase 13 — Agent V2
const AgentListV2 = React.lazy(() => import("./pages/Agent/AgentListV2"));
const EditAgentV2 = React.lazy(() => import("./pages/Agent/EditAgentV2"));

// Phase 13 — Account/Users V2
const UserListV2 = React.lazy(() => import("./pages/Account/Users/UserListV2"));
const AddUserV2 = React.lazy(() => import("./pages/Account/Users/AddUserV2"));
const UpdateUserV2 = React.lazy(() => import("./pages/Account/Users/UpdateUserV2"));

// Agent (parent layout)
const Agent = React.lazy(() => import("./pages/Agent"));

// Agent Activity
const AgentActivity = React.lazy(() => import("./pages/AgentActivity"));
const AgentBankWithdrawalV2 = React.lazy(() => import("./pages/AgentActivity/BankWithdrawalV2"));
const AgentSplitMoneyV2 = React.lazy(() => import("./pages/AgentActivity/SplitMoneyV2"));

// Employee (מסך Add הוסר ב-CR-35 — יצירת עובד דרך ניהול משתמשים account/create-user)
const Employee = React.lazy(() => import("./pages/Employee"));
const EmployeeListV2 = React.lazy(() => import("./pages/Employee/EmployeeListV2"));
const EditEmployeeV2 = React.lazy(() => import("./pages/Employee/EditEmployeeV2"));
const WithdrawalChargesPageV2 = React.lazy(() => import("./pages/Employee/WithdrawalChargesPageV2"));

// Employee Activity
const EmployeeActivity = React.lazy(() => import("./pages/EmployeeActivity"));
const ListEmployeeTerminalsV2 = React.lazy(() => import("./pages/EmployeeActivity/ListEmployeeTerminalsV2"));
const AlternativeLoadingTerminalsV2 = React.lazy(() => import("./pages/EmployeeActivity/AlternativeLoadingTerminalsV2"));
const EmployeeLoadsListV2 = React.lazy(() => import("./pages/EmployeeActivity/EmployeeLoadsListV2"));
const BankWithdrawalV2 = React.lazy(() => import("./pages/EmployeeActivity/BankWithdrawalV2"));
const ChargingTerminalV2 = React.lazy(() => import("./pages/EmployeeActivity/ChargingTerminalV2"));
const EmpSplitMoneyV2 = React.lazy(() => import("./pages/EmployeeActivity/SplitMoneyV2"));
const WithdrawalTerminalV2 = React.lazy(() => import("./pages/EmployeeActivity/WithdrawalTerminalV2"));

// Locations
const Location = React.lazy(() => import("./pages/Location"));
const LocationListV2 = React.lazy(() => import("./pages/Location/LocationListV2"));
const EditLocationV2 = React.lazy(() => import("./pages/Location/EditLocationV2"));
const AddLocationV2 = React.lazy(() => import("./pages/Location/AddLocationV2"));

// Machines/Terminals
const Machines = React.lazy(() => import("./pages/Machines"));
const TerminalPage = React.lazy(() => import("./pages/Machines/TerminalPage"));
const MachineListV2 = React.lazy(() => import("./pages/Machines/MachineListV2"));
const EditMachineV2 = React.lazy(() => import("./pages/Machines/EditMachineV2"));
const AddMachineV2 = React.lazy(() => import("./pages/Machines/AddMachineV2"));

// Users (parent)
const Users = React.lazy(() => import("./pages/Account/Users"));

// Unknown Route
const Error404 = React.lazy(() => import("./pages/Utility/Error404"));

// Cash Expense
const CashExpenseV2 = React.lazy(() => import("./pages/CashExpense/indexV2"));
const DashboardV2 = React.lazy(() => import("./pages/Dashboard/indexV2"));

// Special Days
const SpecialDays = React.lazy(() => import("./pages/SpecialDays"));
const SpecialDaysListV2 = React.lazy(() => import("./pages/SpecialDays/ListV2"));
const AddSpecialDaysV2 = React.lazy(() => import("./pages/SpecialDays/FormV2").then(m => ({ default: m.AddSpecialDaysV2 })));
const UpdateSpecialDaysV2 = React.lazy(() => import("./pages/SpecialDays/FormV2").then(m => ({ default: m.UpdateSpecialDaysV2 })));

// Profitability — דפים כבדים (xlsx)
const ProfitabilityPage = React.lazy(() => import("./pages/Profitability/ProfitabilityPage"));
const ProfitabilityDemo = React.lazy(() => import("./pages/Profitability/ProfitabilityDemo"));
const MonthlyExpensesV2 = React.lazy(() => import("./pages/Profitability/MonthlyExpensesV2"));
const MonthlyReportV2 = React.lazy(() => import("./pages/Profitability/MonthlyReportV2"));
const RevenueInvoicesV2 = React.lazy(() => import("./pages/Profitability/RevenueInvoicesV2"));
const PaymentScheduleV2 = React.lazy(() => import("./pages/Profitability/PaymentScheduleV2"));
const InvoiceTrackingV2 = React.lazy(() => import("./pages/Profitability/InvoiceTrackingV2"));
const InvoiceScanV2 = React.lazy(() => import("./pages/Profitability/InvoiceScanV2"));
const LocationGroupsV2 = React.lazy(() => import("./pages/Profitability/LocationGroupsV2"));
const EmployeeSalaryV2 = React.lazy(() => import("./pages/Profitability/EmployeeSalaryV2"));

// Reports — דפים כבדים (xlsx, charts)
const ReportPage = React.lazy(() => import("./pages/Report/ReportPage"));

// Phase 14 — Reports V2
const AgentCashExpenseV2 = React.lazy(() => import("./pages/Report/AgentCashExpenseV2"));
const ReferenceReportV2 = React.lazy(() => import("./pages/Report/ReferenceReportV2"));
const TerminalReportV2 = React.lazy(() => import("./pages/Report/TerminalReportV2"));
const WithdrawalReportV2 = React.lazy(() => import("./pages/Report/WithdrawalReportV2"));
const LoadingGapReportV2 = React.lazy(() => import("./pages/Report/LoadingGapReportV2"));
const EmployeeLoadingGapsCumulativeReportV2 = React.lazy(() => import("./pages/Report/EmployeeLoadingGapsCumulativeReportV2"));
const CheckFlowReportV2 = React.lazy(() => import("./pages/Report/CheckFlowReportV2"));
const MoneyTrailReportV2 = React.lazy(() => import("./pages/Report/MoneyTrailReportV2"));
const EmployeeBalanceReportV2 = React.lazy(() => import("./pages/Report/EmployeeBalanceReportV2"));
const BatchLinesReconciliationReportV2 = React.lazy(() => import("./pages/Report/BatchLinesReconciliationReportV2"));
const CasponetInvoiceVerificationV2 = React.lazy(() => import("./pages/Report/CasponetInvoiceVerificationV2"));
const MissingInvoiceBatchesReportV2 = React.lazy(() => import("./pages/Report/MissingInvoiceBatchesReportV2"));

// SysAdmin V2 (Phase 15) — all legacy removed in Part C
const SysAdminDashboardV2 = React.lazy(() => import("./pages/SysAdmin/DashboardV2"));
const CompanySetupV2 = React.lazy(() => import("./pages/SysAdmin/CompanySetupV2"));
const BillingReportV2 = React.lazy(() => import("./pages/SysAdmin/BillingReportV2"));
const CalendarPageV2 = React.lazy(() => import("./pages/SysAdmin/CalendarPageV2"));
const RecognitionPresetsV2 = React.lazy(() => import("./pages/SysAdmin/RecognitionPresetsV2"));
const VatPolicyModesV2 = React.lazy(() => import("./pages/SysAdmin/VatPolicyModesV2"));
const CompanySetupTemplateV2 = React.lazy(() => import("./pages/SysAdmin/CompanySetupTemplateV2"));

function App() {
  const dispatch = useDispatch();
  const location = useLocation();

  const [isRTLMode, setIsRTLMode] = useState(false);

  const userInfo = LocalstorageService.getItem(AuthUserInfo) || null;

  // Set direction for LTR/RTL directions.
  useEffect(() => {
    userInfo?.data && dispatch(setAuthUser(userInfo?.data));
    userInfo?.token && dispatch(setAuthToken(userInfo?.token));
    const dir = ["he"].includes(i18n.resolvedLanguage) ? "rtl" : "ltr";
    const htmlElem = document.getElementsByTagName("html");
    const bodyElem = document.getElementsByTagName("body");

    if (htmlElem && bodyElem && dir === "rtl") {
      htmlElem[0].setAttribute("dir", "rtl");
      bodyElem[0].style.textAlign = "right";
      bodyElem[0].classList.add("rtl-mode");
      setIsRTLMode(true);
    } else {
      htmlElem[0].setAttribute("dir", "");
      bodyElem[0].style.textAlign = "";
      bodyElem[0].classList.remove("rtl-mode");
      setIsRTLMode(false);
    }

    dispatch(changeRTLLayout({ isRtlLayout: dir === "rtl" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update document title
  useEffect(() => {
    const matchPath = [
      "/account/users/",
      "/verify-email/",
      "/terminal/",
      "/bank/",
      "/company/",
      "/agent/",
      "/employee/",
      "/locations/",
      "/special-days/",
      "/loading-terminal/",
      "/cash-expense/",
      "/report/",
      "/sysadmin/",
    ];

    let routePath = location.pathname;

    // 22/05/2026 — V2 routes inherit legacy titles: strip trailing -v2 (with optional /id segments)
    // e.g. /profitability/invoice-scan-v2 → /profitability/invoice-scan
    //      /employee/update-v2/15 → /employee/update/15
    //      /locations/list-v2 → /locations/list
    const legacyPath = routePath
      .replace(/-v2(\/|$)/g, '$1')   // remove '-v2' before slash or end
      .replace(/\/v2(\/|$)/g, '$1'); // also handle '/v2' segment style

    const idx = matchPath.findIndex((v) => legacyPath.startsWith(v));
    const lookupKey = idx !== -1 ? matchPath[idx] + "*" : legacyPath;
    updateAppTitle(RouteTitles[lookupKey] || RouteTitles[routePath] || "ATM Management");
  }, [location]);

  return (
    <React.Fragment>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Phase 1.D Component Library — public demo (no auth) */}
          <Route path="/profitability-demo" element={<ProfitabilityDemo />} />

          <Route
            path="/"
            element={
              <RouteAuthGuard
                isAuthProtected={true}
                isLockScreen={!!LocalstorageService.getItem(IsLockScreen)}
              >
                <VerticalLayout />
              </RouteAuthGuard>
            }
          >
            <Route path="/dashboard" element={<DashboardV2 />} />
            <Route path="/dashboard-v2" element={<LegacyV2Redirect />} />

            <Route path="/bank" element={<Bank />}>
              <Route path="list" element={<BankListV2 />} />
              <Route path="add" element={<AddBankV2 />} />
              <Route path="update/:bankId" element={<UpdateBankV2 />} />
              <Route path=":bankId/branches" element={<BankBranchV2 />} />

              {/* Part C — redirects ל-bookmarks ישנים (להסרה אחרי 1-2 גרסאות) */}
              <Route path="list-v2" element={<LegacyV2Redirect />} />
              <Route path="add-v2" element={<LegacyV2Redirect />} />
              <Route path="update-v2/:bankId" element={<LegacyV2Redirect />} />
              <Route path=":bankId/branches-v2" element={<LegacyV2Redirect />} />
            </Route>

            <Route path="/company/list" element={<Company />} />

            {/* Admin Routes */}
            <Route path="/admin/special-days-config" element={<SpecialDaysConfigV2 />} />
            <Route path="/admin/company-parameters" element={<CompanyParametersV2 />} />
            <Route path="/admin/terminal-correction" element={<TerminalOperationCorrectionV2 />} />
            <Route path="/admin/send-message" element={<ModuleRouteGuard><SendMessageV2 /></ModuleRouteGuard>} />
            <Route path="/admin/alerts" element={<AlertsHubV2 />} />

            {/* Part C — redirects (V2 → canonical) */}
            <Route path="/admin/special-days-config-v2" element={<LegacyV2Redirect />} />
            <Route path="/admin/company-parameters-v2" element={<LegacyV2Redirect />} />
            <Route path="/admin/terminal-correction-v2" element={<LegacyV2Redirect />} />
            <Route path="/admin/send-message-v2" element={<LegacyV2Redirect />} />
            <Route path="/admin/alerts-v2" element={<LegacyV2Redirect />} />
            {/* Part C — 3 מסכי legacy מאוחדים ל-alerts (לשמירת bookmarks) */}
            <Route path="/admin/message-role-mapping" element={<Navigate to="/admin/alerts" replace />} />
            <Route path="/admin/notification-preferences" element={<Navigate to="/admin/alerts" replace />} />
            <Route path="/admin/shabbat-settings" element={<Navigate to="/admin/alerts" replace />} />

            <Route path="/agent" element={<Agent />}>
              <Route path="list" element={<AgentListV2 />} />
              <Route path="update/:agentId" element={<EditAgentV2 />} />

              {/* Part C — redirects */}
              <Route path="list-v2" element={<LegacyV2Redirect />} />
              <Route path="update-v2/:agentId" element={<LegacyV2Redirect />} />
            </Route>

            <Route path="/agent-activity" element={<AgentActivity />}>
              <Route path="bank-withdrawal" element={<AgentBankWithdrawalV2 />} />
              <Route path="split-money" element={<AgentSplitMoneyV2 />} />

              {/* Part C — redirects */}
              <Route path="bank-withdrawal-v2" element={<LegacyV2Redirect />} />
              <Route path="split-money-v2" element={<LegacyV2Redirect />} />
            </Route>

            <Route path="/employee" element={<Employee />}>
              <Route path="list" element={<EmployeeListV2 />} />
              <Route path="update/:employeeId" element={<EditEmployeeV2 />} />
              <Route path="update/:employeeId/withdrawal/:withdrawalId" element={<WithdrawalChargesPageV2 />} />

              {/* Part C — redirects */}
              <Route path="list-v2" element={<LegacyV2Redirect />} />
              <Route path="update-v2/:employeeId" element={<LegacyV2Redirect />} />
              <Route path="update-v2/:employeeId/withdrawal/:withdrawalId" element={<LegacyV2Redirect />} />
            </Route>

            <Route path="/employee-activity" element={<EmployeeActivity />}>
              <Route path="bank-withdrawal" element={<BankWithdrawalV2 />} />
              <Route path="charging-terminal" element={<ChargingTerminalV2 />} />
              <Route path="split-money" element={<EmpSplitMoneyV2 />} />
              <Route path="withdrawal-terminal" element={<WithdrawalTerminalV2 />} />

              {/* Part C — redirects */}
              <Route path="bank-withdrawal-v2" element={<LegacyV2Redirect />} />
              <Route path="charging-terminal-v2" element={<LegacyV2Redirect />} />
              <Route path="split-money-v2" element={<LegacyV2Redirect />} />
              <Route path="withdrawal-terminal-v2" element={<LegacyV2Redirect />} />
            </Route>

            <Route path="/locations" element={<Location />}>
              <Route path="list" element={<LocationListV2 />} />
              <Route path="add" element={<AddLocationV2 />} />
              <Route path="update/:locationId" element={<EditLocationV2 />} />

              {/* Part C — redirects */}
              <Route path="list-v2" element={<LegacyV2Redirect />} />
              <Route path="add-v2" element={<LegacyV2Redirect />} />
              <Route path="update-v2/:locationId" element={<LegacyV2Redirect />} />
            </Route>

            <Route path="/terminal" element={<Machines />}>
              <Route path="list" element={<MachineListV2 />} />
              <Route path="add" element={<AddMachineV2 />} />
              <Route path="update/:machineId" element={<EditMachineV2 />} />

              {/* Part C — redirects */}
              <Route path="list-v2" element={<LegacyV2Redirect />} />
              <Route path="add-v2" element={<LegacyV2Redirect />} />
              <Route path="update-v2/:machineId" element={<LegacyV2Redirect />} />
            </Route>

            <Route path="/loading-terminal" element={<TerminalPage />}>
              <Route path="list" element={<ListEmployeeTerminalsV2 />} />
              <Route path="terminal-loads" element={<EmployeeLoadsListV2 />} />
              <Route path="alternative" element={<AlternativeLoadingTerminalsV2 />} />

              {/* Part C — redirects */}
              <Route path="list-v2" element={<LegacyV2Redirect />} />
              <Route path="alternative-v2" element={<LegacyV2Redirect />} />
              <Route path="terminal-loads-v2" element={<LegacyV2Redirect />} />
            </Route>

            <Route path="/cash-expense/list" element={<CashExpenseV2 />} />

            {/* Part C — redirect */}
            <Route path="/cash-expense/list-v2" element={<LegacyV2Redirect />} />

            <Route path="/profitability" element={<ProfitabilityPage />}>
              <Route path="demo" element={<ProfitabilityDemo />} />
              <Route path="monthly-report" element={<MonthlyReportV2 />} />
              <Route path="salary" element={<EmployeeSalaryV2 />} />
              <Route path="payment-schedule" element={<PaymentScheduleV2 />} />
              <Route path="expenses" element={<MonthlyExpensesV2 />} />
              <Route path="invoices" element={<InvoiceTrackingV2 />} />
              <Route path="invoice-scan" element={<InvoiceScanV2 />} />
              <Route path="location-groups" element={<LocationGroupsV2 />} />
              <Route path="revenue-invoices" element={<RevenueInvoicesV2 />} />

              {/* Part C — redirects */}
              <Route path="expenses-v2" element={<LegacyV2Redirect />} />
              <Route path="monthly-report-v2" element={<LegacyV2Redirect />} />
              <Route path="payment-schedule-v2" element={<LegacyV2Redirect />} />
              <Route path="invoices-v2" element={<LegacyV2Redirect />} />
              <Route path="invoice-scan-v2" element={<LegacyV2Redirect />} />
              <Route path="location-groups-v2" element={<LegacyV2Redirect />} />
              <Route path="revenue-invoices-v2" element={<LegacyV2Redirect />} />
              {/* employee-salary-v2 → /profitability/salary (path differs from V2 component name) */}
              <Route path="employee-salary-v2" element={<Navigate to="/profitability/salary" replace />} />
            </Route>

            <Route path="/report" element={<ReportPage />}>
              <Route path="agent-cash-expense" element={<AgentCashExpenseV2 />} />
              <Route path="reference" element={<ReferenceReportV2 />} />
              <Route path="terminal" element={<TerminalReportV2 />} />
              <Route path="withdrawal-count" element={<WithdrawalReportV2 />} />
              <Route path="loading-gap" element={<LoadingGapReportV2 />} />
              <Route path="employee-loading-gaps-cumulative" element={<EmployeeLoadingGapsCumulativeReportV2 />} />
              <Route path="check-flow" element={<CheckFlowReportV2 />} />
              <Route path="money-trail" element={<MoneyTrailReportV2 />} />
              <Route path="employee-balance" element={<EmployeeBalanceReportV2 />} />
              <Route path="batch-lines-reconciliation" element={<BatchLinesReconciliationReportV2 />} />
              <Route path="casponet-invoice-verification" element={<CasponetInvoiceVerificationV2 />} />
              <Route path="missing-invoice-batches" element={<MissingInvoiceBatchesReportV2 />} />

              {/* Part C — redirects */}
              <Route path="agent-cash-expense-v2" element={<LegacyV2Redirect />} />
              <Route path="reference-v2" element={<LegacyV2Redirect />} />
              <Route path="terminal-v2" element={<LegacyV2Redirect />} />
              <Route path="withdrawal-count-v2" element={<LegacyV2Redirect />} />
              <Route path="loading-gap-v2" element={<LegacyV2Redirect />} />
              <Route path="employee-loading-gaps-cumulative-v2" element={<LegacyV2Redirect />} />
              <Route path="check-flow-v2" element={<LegacyV2Redirect />} />
              <Route path="employee-balance-v2" element={<LegacyV2Redirect />} />
              <Route path="batch-lines-reconciliation-v2" element={<LegacyV2Redirect />} />
              <Route path="missing-invoice-batches-v2" element={<LegacyV2Redirect />} />
            </Route>

            <Route path="/special-days" element={<SpecialDays />}>
              <Route path="list" element={<SpecialDaysListV2 />} />
              <Route path="add" element={<AddSpecialDaysV2 />} />
              <Route path="update/:dayId" element={<UpdateSpecialDaysV2 />} />

              {/* Part C — redirects */}
              <Route path="list-v2" element={<LegacyV2Redirect />} />
              <Route path="add-v2" element={<LegacyV2Redirect />} />
              <Route path="update-v2/:dayId" element={<LegacyV2Redirect />} />
            </Route>

            <Route path="/account/profile" element={<UserProfile />} />

            <Route path="/account/users" element={<Users />}>
              <Route path="list" element={<UserListV2 />} />
              <Route path="add" element={<AddUserV2 />} />
              <Route path="update/:userId" element={<UpdateUserV2 />} />

              {/* Part C — redirects */}
              <Route path="list-v2" element={<LegacyV2Redirect />} />
              <Route path="add-v2" element={<LegacyV2Redirect />} />
              <Route path="update-v2/:userId" element={<LegacyV2Redirect />} />
            </Route>
          </Route>

          {/* SysAdmin Routes */}
          <Route
            path="/sysadmin"
            element={
              <RouteAuthGuard isAuthProtected={true}>
                <SysAdminLayout />
              </RouteAuthGuard>
            }
          >
            <Route path="dashboard" element={<SysAdminDashboardV2 />} />
            <Route path="company/create" element={<CompanySetupV2 />} />
            <Route path="company/:companyId" element={<CompanySetupV2 />} />
            <Route path="companies" element={<Company />} />
            <Route path="special-days-config" element={<SpecialDaysConfigV2 />} />
            <Route path="billing" element={<BillingReportV2 />} />
            <Route path="calendar" element={<CalendarPageV2 />} />
            <Route path="recognition-presets" element={<RecognitionPresetsV2 />} />
            <Route path="vat-policy-modes" element={<VatPolicyModesV2 />} />
            <Route path="company-setup-template" element={<CompanySetupTemplateV2 />} />

            {/* Part C — redirects */}
            <Route path="dashboard-v2" element={<LegacyV2Redirect />} />
            <Route path="company-v2/create" element={<LegacyV2Redirect />} />
            <Route path="company-v2/:companyId" element={<LegacyV2Redirect />} />
            <Route path="billing-v2" element={<LegacyV2Redirect />} />
            <Route path="calendar-v2" element={<LegacyV2Redirect />} />
            <Route path="recognition-presets-v2" element={<LegacyV2Redirect />} />
            <Route path="vat-policy-modes-v2" element={<LegacyV2Redirect />} />
            <Route path="company-setup-template-v2" element={<LegacyV2Redirect />} />
            {/* Part C — D.2: /sysadmin/bank/* (legacy nested) → /bank/* (canonical V2) */}
            <Route path="bank/*" element={<SysAdminBankRedirect />} />
          </Route>

          <Route
            path="/login"
            element={
              <RouteAuthGuard isAuthProtected={false}>
                <NonAuthLayout>
                  <Login />
                </NonAuthLayout>
              </RouteAuthGuard>
            }
          />

          <Route
            path="/lock-screen"
            element={
              <RouteAuthGuard isAuthProtected={true}>
                <NonAuthLayout>
                  <Lockscreen />
                </NonAuthLayout>
              </RouteAuthGuard>
            }
          />

          <Route
            path="/forgot-password"
            element={
              <RouteAuthGuard isAuthProtected={false}>
                <NonAuthLayout>
                  <ForgotPassword />
                </NonAuthLayout>
              </RouteAuthGuard>
            }
          />

          <Route
            path="/reset-password"
            element={
              <RouteAuthGuard isAuthProtected={false}>
                <NonAuthLayout>
                  <ResetPassword />
                </NonAuthLayout>
              </RouteAuthGuard>
            }
          />

          <Route
            path="/verify-email/:userId"
            element={
              <RouteAuthGuard isAuthProtected={false}>
                <NonAuthLayout>
                  <VerifyEmail />
                </NonAuthLayout>
              </RouteAuthGuard>
            }
          />

          <Route
            path="*"
            element={
              <NonAuthLayout>
                <Error404 />
              </NonAuthLayout>
            }
          />
        </Routes>
      </Suspense>

      <ToastContainer
        autoClose={3000}
        hideProgressBar
        newestOnTop
        closeOnClick
        theme="colored"
        pauseOnFocusLoss={false}
        rtl={isRTLMode}
      />
    </React.Fragment>
  );
}

export default App;
